import React, { useState, useMemo, useEffect } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CircleIcon from '@mui/icons-material/Circle';
import {
  useGetAllPostsQuery,
  useGetPlacesQuery,
  useGetLocationsQuery,
  useUpdateUserMutation,
  useUploadUserFacePictureMutation,
  useUploadUserPublicPictureMutation,
  useGetUserByIdQuery,
} from '../../redux/adminApiSlice';
import { convertToProxyUrl } from '../../utils/photoProxy';
import { User, Location, Place } from '../../types/interfaces';
import DisplayJSON from '../DisplayJSON/DisplayJSON';
import { gridTheme, gridTokens } from '../../theme/gridTheme';

export type UserModalTab = 'info' | 'posts' | 'places' | 'neighborhoods';

export interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onUserUpdate?: (updatedUser: User) => void; // Callback when user is updated
  /** Open on a specific tab (e.g. places from the All Users Places column). */
  initialTab?: UserModalTab;
}

const LOCATION_ICONS: Record<string, string> = {
  place: '📍',
  neighborhood: '🏘️',
  city: '🌆',
  state: '🗺️',
  country: '🌎',
};

type ActivityLevel = 'power' | 'regular' | 'occasional' | 'dormant' | 'new';

const ACTIVITY_META: Record<ActivityLevel, { label: string; color: string; blurb: string }> = {
  power: { label: 'Power user', color: '#a855f7', blurb: 'Active and posting often — no nudge needed.' },
  regular: { label: 'Regular', color: gridTokens.success, blurb: 'Comes back regularly; light touch.' },
  occasional: {
    label: 'Occasional',
    color: '#38bdf8',
    blurb: 'In and out — a nudge could prompt a post.',
  },
  dormant: {
    label: 'Dormant',
    color: '#9ca3af',
    blurb: 'Gone quiet — a notification may re-engage (or annoy).',
  },
  new: { label: 'New', color: '#f59e0b', blurb: 'Just signed up — give it a beat before nudging.' },
};

/** Same heuristic as the backend audience service (kept in sync). */
function deriveActivityLevel(params: {
  lastActive: Date | null;
  postCount: number;
  accountAgeDays: number;
  postsPerWeek: number;
}): ActivityLevel {
  const { lastActive, postCount, accountAgeDays, postsPerWeek } = params;
  if (accountAgeDays < 2 && postCount === 0) return 'new';
  if (!lastActive) return 'dormant';
  const daysSinceActive = (Date.now() - lastActive.getTime()) / (24 * 60 * 60 * 1000);
  if (daysSinceActive > 14) return 'dormant';
  if (postsPerWeek >= 3 && daysSinceActive <= 2) return 'power';
  if (daysSinceActive <= 3 && (postCount > 0 || postsPerWeek >= 1)) return 'regular';
  return 'occasional';
}

function fmtRelative(d: Date | null): string {
  if (!d || Number.isNaN(d.getTime())) return 'Never';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${(days / 365).toFixed(1)}y ago`;
}

function fmtAge(days: number): string {
  if (days < 1) {
    const hours = Math.round(days * 24);
    return hours <= 1 ? 'today' : `${hours}h`;
  }
  if (days < 30) return `${Math.round(days)}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${(days / 365).toFixed(1)}y`;
}

/** Tight uppercase section header, matching the console's table-head style. */
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      fontWeight: 700,
      color: gridTokens.textTertiary,
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  user,
  onUserUpdate,
  initialTab = 'info',
}) => {
  const [userModalTab, setUserModalTab] = useState<UserModalTab>(initialTab);
  const [isEditing, setIsEditing] = useState(false);
  const [showJsonDialog, setShowJsonDialog] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Apply requested tab whenever the modal opens (or initialTab changes while open)
  useEffect(() => {
    if (isOpen) {
      setUserModalTab(initialTab);
    }
  }, [isOpen, initialTab, user?._id]);

  // Editable fields state
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [bio, setBio] = useState('');
  const [gender, setGender] = useState('');
  const [race, setRace] = useState('');
  const [sexualOrientation, setSexualOrientation] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [isMockUser, setIsMockUser] = useState(false);
  const [customStatus, setCustomStatus] = useState('');

  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [uploadFacePicture] = useUploadUserFacePictureMutation();
  const [uploadPublicPicture] = useUploadUserPublicPictureMutation();

  // Get user data with refetch capability
  const { data: fetchedUser, refetch: refetchUser } = useGetUserByIdQuery(
    user?._id || '',
    { skip: !user?._id || !isOpen }
  );

  // Use fetched user if available, otherwise use prop user
  const currentUser = fetchedUser || user;

  // Helper to extract content from privacy-wrapped fields
  const extractContent = (field: any): string => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object' && 'content' in field) return field.content || '';
    return String(field);
  };

  // Initialize form fields when user changes
  useEffect(() => {
    if (currentUser) {
      setDisplayName(extractContent(currentUser.displayName));
      setUsername(currentUser.username || '');
      setEmail(currentUser.email || '');
      setBio(extractContent(currentUser.bio) || '');
      setGender(extractContent(currentUser.gender) || '');
      setRace(extractContent(currentUser.race) || '');
      setSexualOrientation(extractContent(currentUser.sexualOrientation) || '');
      setTags(
        Array.isArray(currentUser.tags)
          ? currentUser.tags
              .map((tag: any) => {
                if (typeof tag === 'string') return tag;
                if (tag && typeof tag === 'object' && 'content' in tag) return String(tag.content || '');
                return '';
              })
              .map((tag: string) => tag.trim())
              .filter(Boolean)
          : []
      );
      setRole(currentUser.role || 'user');
      setIsMockUser(currentUser.isMockUser || false);
      setCustomStatus(currentUser.customStatus || '');
      setIsEditing(false);
    }
  }, [currentUser]);

  // Get user's posts
  const { data: userPostsData, isLoading: isLoadingPosts, error: postsError } = useGetAllPostsQuery(
    {
      userFilter: currentUser?._id ? [currentUser._id] : undefined,
      limit: 1000,
    },
    { skip: !currentUser?._id }
  );

  // Get all places to filter by user's joined places
  const { data: allPlaces = [] } = useGetPlacesQuery(undefined, {
    skip: !currentUser?._id || !isOpen,
  });

  // Get all locations to find neighborhoods and resolve location names
  const { data: allLocations = [] } = useGetLocationsQuery(undefined, {
    skip: !currentUser?._id || !isOpen,
  });

  // Get all posts to find neighborhoods user has posted in
  const { data: allPostsData } = useGetAllPostsQuery(
    {
      userFilter: currentUser?._id ? [currentUser._id] : undefined,
      limit: 1000,
    },
    { skip: !currentUser?._id || userModalTab !== 'neighborhoods' }
  );

  // Calculate joined/owned places
  const userPlaces = useMemo(() => {
    if (!currentUser || !allPlaces.length) return [];

    const toPlaceId = (value: unknown): string | null => {
      if (!value) return null;
      if (typeof value === 'string') return value;
      if (typeof value === 'object') {
        const obj = value as { _id?: unknown; place?: unknown };
        if (obj._id != null) return String(obj._id);
        if (obj.place != null) return toPlaceId(obj.place);
      }
      return null;
    };

    const joinedPlaceIds = Array.isArray(currentUser.joinedPlaces)
      ? (currentUser.joinedPlaces.map(toPlaceId).filter(Boolean) as string[])
      : [];

    return allPlaces.filter((place) => {
      const placeId = place._id?.toString();
      const isJoined = joinedPlaceIds.some((id) => id === placeId);
      const isCreator = place.creator?.toString() === currentUser._id?.toString();
      return isJoined || isCreator;
    });
  }, [currentUser, allPlaces]);

  // Calculate neighborhoods user has posted in
  const neighborhoodsPostedIn = useMemo(() => {
    if (!allPostsData?.data || !allLocations.length) return [];

    const neighborhoodIds = new Set<string>();

    allPostsData.data.forEach((post: any) => {
      if (post.location?.neighborhood?.id) {
        neighborhoodIds.add(post.location.neighborhood.id.toString());
      }
      if (post.visibilityLevels) {
        post.visibilityLevels.forEach((vl: any) => {
          if (vl.level === 'NEIGHBORHOOD' && vl.locationId) {
            neighborhoodIds.add(vl.locationId.toString());
          }
        });
      }
    });

    return allLocations.filter(
      (loc) => loc.level === 'NEIGHBORHOOD' && neighborhoodIds.has(loc._id?.toString() || '')
    );
  }, [allPostsData, allLocations]);

  // Handle saving user updates
  const handleSaveUser = async () => {
    if (!currentUser?._id) return;

    try {
      const updates: any = {};

      const currentDisplayName = extractContent(currentUser.displayName);
      const currentBio = extractContent(currentUser.bio);
      const currentGender = extractContent(currentUser.gender);
      const currentRace = extractContent(currentUser.race);
      const currentSexualOrientation = extractContent(currentUser.sexualOrientation);

      if (displayName !== currentDisplayName) updates.displayName = displayName;
      if (username !== (currentUser.username || '')) updates.username = username;
      if (email !== (currentUser.email || '')) updates.email = email;
      if (bio !== currentBio) updates.bio = bio;
      if (gender !== currentGender) updates.gender = gender;
      if (race !== currentRace) updates.race = race;
      if (sexualOrientation !== currentSexualOrientation)
        updates.sexualOrientation = sexualOrientation;
      const currentTags = Array.isArray(currentUser.tags)
        ? currentUser.tags
            .map((tag: any) => {
              if (typeof tag === 'string') return tag;
              if (tag && typeof tag === 'object' && 'content' in tag) return String(tag.content || '');
              return '';
            })
            .map((tag: string) => tag.trim())
            .filter(Boolean)
        : [];
      if (JSON.stringify(tags) !== JSON.stringify(currentTags)) updates.tags = tags;
      if (role !== (currentUser.role || 'user')) updates.role = role;
      if (isMockUser !== (currentUser.isMockUser || false)) updates.isMockUser = isMockUser;
      if (customStatus !== (currentUser.customStatus || '')) updates.customStatus = customStatus;

      if (Object.keys(updates).length === 0) {
        setToast({ msg: 'No changes to save', severity: 'error' });
        return;
      }

      const updatedUser = await updateUser({ id: currentUser._id, updates }).unwrap();
      setIsEditing(false);
      await refetchUser();

      if (onUserUpdate && updatedUser) {
        onUserUpdate(updatedUser as User);
      }
      setToast({ msg: 'User updated successfully', severity: 'success' });
    } catch (error) {
      console.error('Error updating user:', error);
      setToast({ msg: 'Failed to update user', severity: 'error' });
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (file: File, isFacePicture: boolean) => {
    if (!currentUser?._id) return;

    try {
      if (isFacePicture) {
        await uploadFacePicture({ userId: currentUser._id, photo: file }).unwrap();
      } else {
        await uploadPublicPicture({ userId: currentUser._id, photo: file }).unwrap();
      }

      const refetchedData = await refetchUser();
      if (onUserUpdate && refetchedData.data) {
        onUserUpdate(refetchedData.data as User);
      }
      setToast({ msg: 'Photo uploaded successfully', severity: 'success' });
    } catch (error) {
      console.error('Error uploading photo:', error);
      setToast({ msg: 'Failed to upload photo', severity: 'error' });
    }
  };

  // Handle tag input
  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  // Helper function to get location name
  const getLocationName = (locationRef: string | Location | Place | undefined): string => {
    if (!locationRef) return '';
    if (typeof locationRef === 'string') {
      const foundLocation = allLocations.find((loc) => loc._id === locationRef);
      if (foundLocation) return foundLocation.name;
      const foundPlace = allPlaces.find((place) => place._id === locationRef);
      if (foundPlace) return foundPlace.name;
      return '';
    }
    if (typeof locationRef === 'object' && 'name' in locationRef) {
      return locationRef.name;
    }
    return '';
  };

  if (!isOpen || !currentUser) return null;

  const userData = currentUser;
  // Face may fall back to legacy profilePicture; public must stay independent
  // so an empty public pic doesn't mirror the face via the same fallback.
  const facePictureUrl = convertToProxyUrl(userData.facePicture || userData.profilePicture);
  const publicPictureUrl = convertToProxyUrl(userData.publicPicture);
  const displayNameValue = extractContent(userData.displayName);
  const bioValue = extractContent(userData.bio);

  const postsCount = userPostsData?.data?.length || userPostsData?.total || 0;

  // ---- Derived activity signals (mirror the backend audience heuristic) ----
  const createdAtDate = userData.createdAt ? new Date(userData.createdAt) : null;
  const lastActiveDate = userData.lastActive ? new Date(userData.lastActive) : null;
  const lastPostDate = (() => {
    const dates = (userPostsData?.data || [])
      .map((p: any) => (p.createdAt ? new Date(p.createdAt).getTime() : 0))
      .filter(Boolean);
    return dates.length ? new Date(Math.max(...dates)) : null;
  })();
  const accountAgeDays = createdAtDate
    ? Math.max(0, (Date.now() - createdAtDate.getTime()) / (24 * 60 * 60 * 1000))
    : 0;
  const postsPerWeek =
    postsCount > 0 ? Math.round((postsCount / Math.max(accountAgeDays / 7, 1 / 7)) * 10) / 10 : 0;
  const activityLevel = deriveActivityLevel({
    lastActive: lastActiveDate,
    postCount: postsCount,
    accountAgeDays,
    postsPerWeek,
  });
  const hasPush = !!userData.expoPushToken;

  // Small helpers for consistent read-only / editable fields.
  const readField = (label: string, value: React.ReactNode) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: value ? 'text.primary' : 'text.disabled' }}>
        {value || 'Not set'}
      </Typography>
    </Box>
  );

  const PhotoTile = ({
    label,
    url,
    inputId,
    isFace,
    fallback,
  }: {
    label: string;
    url: string | null | undefined;
    inputId: string;
    isFace: boolean;
    fallback: string;
  }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          borderRadius: `${gridTokens.radius}px`,
          overflow: 'hidden',
          border: `1px solid ${gridTokens.borderPrimary}`,
          bgcolor: gridTokens.bgTertiary,
          '&:hover .photo-overlay': { opacity: 1 },
        }}
      >
        {url ? (
          <Box
            component="img"
            src={url}
            alt={label}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e: any) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 40,
              color: gridTokens.textMuted,
            }}
          >
            {fallback}
          </Box>
        )}
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          id={inputId}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handlePhotoUpload(file, isFace);
          }}
        />
        <Box
          component="label"
          htmlFor={inputId}
          className="photo-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: '#fff',
            opacity: 0,
            transition: 'opacity 0.15s',
            cursor: 'pointer',
          }}
        >
          <PhotoCameraIcon fontSize="small" />
          <Typography variant="caption">Click to change</Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={gridTheme}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        // Sit above other MUI dialogs (e.g. the Analytics bucket drill-in at
        // z-index 1300) so opening this from a dialog renders OVER it.
        sx={{ zIndex: 1400 }}
        PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: `1px solid ${gridTokens.borderPrimary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              src={facePictureUrl || publicPictureUrl || undefined}
              sx={{ width: 40, height: 40, bgcolor: gridTokens.bgTertiary }}
            >
              {(displayNameValue || userData.username || '?').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" noWrap>
                  {displayNameValue || 'Unknown'}
                </Typography>
                {userData.isOnline ? (
                  <Tooltip title="Online now">
                    <CircleIcon sx={{ fontSize: 10, color: gridTokens.success }} />
                  </Tooltip>
                ) : null}
                {userData.isMockUser ? <Chip label="mock" size="small" /> : null}
                {userData.role === 'admin' ? (
                  <Chip label="admin" size="small" color="primary" variant="outlined" />
                ) : null}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                @{userData.username || 'no-username'}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={userData._id}>
              <Chip
                label={`${userData._id.slice(0, 8)}…`}
                size="small"
                variant="outlined"
                sx={{ fontFamily: 'monospace' }}
              />
            </Tooltip>
            <Tooltip title="Copy User ID">
              <IconButton size="small" onClick={() => navigator.clipboard.writeText(userData._id)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View full user JSON">
              <IconButton size="small" onClick={() => setShowJsonDialog(true)}>
                <CodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <DialogContent dividers sx={{ p: 2, borderColor: gridTokens.borderPrimary }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '200px 1fr' },
              gap: 2.5,
              height: '100%',
              minHeight: 0,
            }}
          >
            {/* Left column — photos + edit controls */}
            <Stack spacing={2}>
              <PhotoTile
                label="Face picture"
                url={facePictureUrl}
                inputId="face-picture-upload"
                isFace
                fallback="👤"
              />
              <PhotoTile
                label="Public picture"
                url={publicPictureUrl}
                inputId="public-picture-upload"
                isFace={false}
                fallback="📷"
              />

              {isEditing ? (
                <Stack spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveUser}
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Saving…' : 'Save changes'}
                  </Button>
                  <Button variant="outlined" color="inherit" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                </Stack>
              ) : (
                <Button variant="contained" startIcon={<EditIcon />} onClick={() => setIsEditing(true)}>
                  Edit user
                </Button>
              )}
            </Stack>

            {/* Right column — tabs */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Tabs
                value={userModalTab}
                onChange={(_, v) => setUserModalTab(v)}
                sx={{ borderBottom: `1px solid ${gridTokens.borderPrimary}`, mb: 2 }}
              >
                <Tab value="info" label="Info" />
                <Tab value="posts" label={`Posts (${postsCount})`} />
                <Tab value="places" label={`Places (${userPlaces.length})`} />
                <Tab value="neighborhoods" label={`Neighborhoods (${neighborhoodsPostedIn.length})`} />
              </Tabs>

              <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pr: 0.5 }}>
                {/* ---------------- INFO ---------------- */}
                {userModalTab === 'info' && (
                  <Stack spacing={2}>
                    {/* ---- Analytics: how active + should I nudge them? ---- */}
                    <Box>
                      <SectionLabel>Analytics</SectionLabel>
                      <Box
                        sx={{
                          p: 1.5,
                          border: `1px solid ${gridTokens.borderPrimary}`,
                          borderRadius: `${gridTokens.radius}px`,
                          bgcolor: gridTokens.bgTertiary,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mb: 1.25 }}
                        >
                          <Chip
                            size="small"
                            label={ACTIVITY_META[activityLevel].label}
                            sx={{
                              bgcolor: `${ACTIVITY_META[activityLevel].color}22`,
                              color: ACTIVITY_META[activityLevel].color,
                              fontWeight: 700,
                            }}
                          />
                          {userData.isOnline ? (
                            <Chip
                              size="small"
                              icon={<CircleIcon sx={{ fontSize: 10, color: `${gridTokens.success} !important` }} />}
                              label="Online now"
                              variant="outlined"
                            />
                          ) : null}
                          <Chip
                            size="small"
                            variant="outlined"
                            color={hasPush ? 'primary' : 'default'}
                            label={hasPush ? 'Notifiable · has push' : 'No push token'}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            {ACTIVITY_META[activityLevel].blurb}
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                            gap: 1.25,
                          }}
                        >
                          {readField('Last active', fmtRelative(lastActiveDate))}
                          {readField('Signed up', `${fmtRelative(createdAtDate)} · ${fmtAge(accountAgeDays)} old`)}
                          {readField('Posts', `${postsCount} · ${postsPerWeek}/wk`)}
                          {readField('Last post', fmtRelative(lastPostDate))}
                          {readField('Friends', String(userData.friends?.length || 0))}
                          {readField('Places', String(userPlaces.length))}
                          {readField('Sign-in', userData.authProvider || 'local')}
                          {readField(
                            'Contact',
                            [userData.phone ? 'phone' : null, userData.email ? 'email' : null]
                              .filter(Boolean)
                              .join(' + ') || '—'
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* ---- Location hierarchy (kept as-is) ---- */}
                    <Box>
                      <SectionLabel>Current location</SectionLabel>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {(['place', 'neighborhood', 'city', 'state', 'country'] as const).map(
                          (level, index) => {
                            let loc: string | Location | Place | undefined;
                            if (level === 'place') loc = userData.currentPlace;
                            else if (level === 'neighborhood') loc = userData.currentNeighborhood;
                            else if (level === 'city') loc = userData.currentCity;
                            else if (level === 'state') loc = userData.currentState;
                            else loc = userData.currentCountry;

                            const icon = LOCATION_ICONS[level] || '📍';
                            const locationName = getLocationName(loc);

                            return (
                              <React.Fragment key={level}>
                                {locationName ? (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`${icon} ${locationName}`}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    {icon} {level.charAt(0).toUpperCase() + level.slice(1)}
                                  </Typography>
                                )}
                                {index < 4 ? (
                                  <Typography variant="caption" color="text.disabled">
                                    →
                                  </Typography>
                                ) : null}
                              </React.Fragment>
                            );
                          }
                        )}
                      </Stack>
                    </Box>

                    <Divider />

                    {/* ---- Profile: dense two-column stacked list ---- */}
                    <Box>
                      <SectionLabel>Profile</SectionLabel>
                      {isEditing ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                          <TextField
                            size="small"
                            label="Display name"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                          />
                          <TextField
                            size="small"
                            label="Username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                          />
                          <TextField
                            size="small"
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                          <TextField
                            size="small"
                            label="Custom status"
                            value={customStatus}
                            onChange={(e) => setCustomStatus(e.target.value)}
                          />
                          <TextField
                            size="small"
                            label="Bio"
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                            multiline
                            minRows={2}
                            sx={{ gridColumn: '1 / -1' }}
                          />
                          <TextField
                            size="small"
                            label="Gender"
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                          />
                          <TextField
                            size="small"
                            label="Race"
                            value={race}
                            onChange={(e) => setRace(e.target.value)}
                          />
                          <TextField
                            size="small"
                            label="Sexual orientation"
                            value={sexualOrientation}
                            onChange={(e) => setSexualOrientation(e.target.value)}
                            sx={{ gridColumn: '1 / -1' }}
                          />
                          <TextField
                            select
                            size="small"
                            label="Role"
                            value={role}
                            onChange={(e) => setRole(e.target.value as 'user' | 'admin')}
                          >
                            <MenuItem value="user">User</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                          </TextField>
                          <TextField
                            select
                            size="small"
                            label="Mock user"
                            value={isMockUser ? 'yes' : 'no'}
                            onChange={(e) => setIsMockUser(e.target.value === 'yes')}
                          >
                            <MenuItem value="no">No</MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                          </TextField>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                          {readField('Display name', displayNameValue)}
                          {readField('Username', `@${userData.username || 'no-username'}`)}
                          {readField('Email', userData.email || 'No email')}
                          {readField('Custom status', userData.customStatus)}
                          <Box sx={{ gridColumn: '1 / -1' }}>
                            {readField('Bio', bioValue || 'No bio')}
                          </Box>
                          {readField('Gender', extractContent(userData.gender))}
                          {readField('Race', extractContent(userData.race))}
                          {readField('Sexual orientation', extractContent(userData.sexualOrientation))}
                          {readField('Role', userData.role || 'user')}
                          {readField('Mock user', userData.isMockUser ? 'Yes' : 'No')}
                        </Box>
                      )}
                    </Box>

                    {/* ---- Tags ---- */}
                    <Box>
                      <SectionLabel>Tags</SectionLabel>
                      {isEditing ? (
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Add a tag"
                              value={tagInput}
                              onChange={(e) => setTagInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddTag();
                                }
                              }}
                            />
                            <Button variant="contained" onClick={handleAddTag}>
                              Add
                            </Button>
                          </Stack>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {tags.map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                onDelete={() => handleRemoveTag(tag)}
                              />
                            ))}
                          </Stack>
                        </Stack>
                      ) : userData.tags && userData.tags.length > 0 ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {userData.tags.map((tag, idx) => (
                            <Chip
                              key={idx}
                              size="small"
                              variant="outlined"
                              label={typeof tag === 'string' ? tag : (tag as any).content || tag}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          No tags
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                )}

                {/* ---------------- POSTS ---------------- */}
                {userModalTab === 'posts' &&
                  (isLoadingPosts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : postsError ? (
                    <Typography variant="body2" color="error">
                      Error loading posts: {String(postsError)}
                    </Typography>
                  ) : !userPostsData?.data || userPostsData.data.length === 0 ? (
                    <Typography variant="body2" color="text.disabled">
                      No posts found
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {userPostsData.data.map((post: any) => (
                        <ListRow
                          key={post._id}
                          id={post._id}
                          title={post.text || '(No text)'}
                          subtitle={`${new Date(post.createdAt).toLocaleString()} • Level: ${
                            post.currentPostLevel || '—'
                          }`}
                        />
                      ))}
                    </Stack>
                  ))}

                {/* ---------------- PLACES ---------------- */}
                {userModalTab === 'places' &&
                  (userPlaces.length === 0 ? (
                    <Typography variant="body2" color="text.disabled">
                      No places found
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {userPlaces.map((place: any) => {
                        const isCreator =
                          place.creator?.toString() === userData?._id?.toString();
                        return (
                          <ListRow
                            key={place._id}
                            id={place._id}
                            title={place.name || 'Unnamed Place'}
                            badge={isCreator ? 'Owner' : undefined}
                            subtitle={[place.type ? `Type: ${place.type}` : null, place.address]
                              .filter(Boolean)
                              .join(' • ')}
                          />
                        );
                      })}
                    </Stack>
                  ))}

                {/* ---------------- NEIGHBORHOODS ---------------- */}
                {userModalTab === 'neighborhoods' &&
                  (neighborhoodsPostedIn.length === 0 ? (
                    <Typography variant="body2" color="text.disabled">
                      No neighborhoods found
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {neighborhoodsPostedIn.map((neighborhood: any) => (
                        <ListRow
                          key={neighborhood._id}
                          id={neighborhood._id}
                          title={neighborhood.name || 'Unnamed Neighborhood'}
                          subtitle={`Level: ${neighborhood.level}`}
                        />
                      ))}
                    </Stack>
                  ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* JSON Dialog */}
      <DisplayJSON
        open={showJsonDialog}
        onClose={() => setShowJsonDialog(false)}
        data={userData}
        title="User JSON Details"
        subtitle={displayNameValue || userData._id}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 1600 }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ThemeProvider>
  );
};

/** Compact list row used by the Posts / Places / Neighborhoods tabs. */
const ListRow: React.FC<{
  id: string;
  title: string;
  subtitle?: string;
  badge?: string;
}> = ({ id, title, subtitle, badge }) => (
  <Box
    sx={{
      p: 1.5,
      border: `1px solid ${gridTokens.borderPrimary}`,
      borderRadius: `${gridTokens.radius}px`,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 1.5,
      transition: 'background-color 0.12s',
      '&:hover': { bgcolor: 'rgba(0, 229, 255, 0.04)' },
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" fontWeight={600} noWrap>
          {title}
        </Typography>
        {badge ? <Chip label={badge} size="small" color="secondary" variant="outlined" /> : null}
      </Stack>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}
    </Box>
    <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', flexShrink: 0 }}>
      {id.substring(0, 8)}…
    </Typography>
  </Box>
);

export default UserModal;
